import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock } from 'lucide-react';
import './Controls.css';

const Controls = () => {
    const organizationalControls = [
        {
            title: 'Data Protection Policy',
            description: 'Lorem ipsum odor amet, consectetuer adipiscing elit. Libero porta class integer dis potenti ridiculus ad orci gravida. Habitasse donec nascetur taciti mus quam habitant malesuada congue? Eleifend tempus tempus metus sit praesent netus. Nostra eros molestie ut facilisis fermentum at. Accumsan interdum laoreet semper egestas sociosqu primis lorem. Facilisi sociosqu malesuada urna at malesuada. Senectus feugiat cursus eleifend; tempus integer conubia? Duis parturient varius ipsum diam at et id metus.'
        },
        {
            title: 'Data Classification and Handling Standard',
            description: 'Lorem ipsum odor amet, consectetuer adipiscing elit. Libero porta class integer dis potenti ridiculus ad orci gravida. Habitasse donec nascetur taciti mus quam habitant malesuada congue? Eleifend tempus tempus metus sit praesent netus. Nostra eros molestie ut facilisis fermentum at. Accumsan interdum laoreet semper egestas sociosqu primis lorem. Facilisi sociosqu malesuada urna at malesuada. Senectus feugiat cursus eleifend; tempus integer conubia? Duis parturient varius ipsum diam at et id metus.'
        },
        {
            title: 'Data Encryption Standard',
            description: 'Specifies how encryption must be applied to protect data at rest and in transit.'
        },
        {
            title: 'Identity and Access Management Policy',
            description: 'Outlines rules for managing user identities and controlling access to systems and data.'
        },
        {
            title: 'Vendor & Third-Party Risk Policy',
            description: 'Defines the process for evaluating and mitigating risks associated with external parties.'
        },
        {
            title: 'Risk Management Program',
            description: "Establishes a structured process for identifying, assessing, prioritizing, and mitigating risks to the organization's assets, operations, and objectives."
        },
        {
            title: 'Incident Response Policy & Plan',
            description: 'Establishes the process for detecting, responding to, and recovering from security incidents.'
        },
        {
            title: 'Breach Disclosure Procedures',
            description: 'Outlines the required steps for notifying stakeholders in the event of a data breach.'
        },
        {
            title: 'AI & Cognitive System Security Policy',
            description: 'Sets standards to secure AI systems, including safeguards against adversarial misuse and model compromise.'
        },
        {
            title: 'Secure Development & DevOps Policy',
            description: 'Establishes secure coding and deployment practices throughout the software development lifecycle.'
        },
        {
            title: 'Training & Awareness Policy',
            description: 'Mandates regular education to ensure personnel understand security responsibilities and threats.'
        },
        {
            title: 'Compliance Monitoring',
            description: 'Ensures ongoing evaluation of adherence to internal policies and external regulatory requirements.'
        }
    ];

    const technicalControls = [
        {
            title: 'MFA (Multi-Factor Authentication)',
            description: 'Adds a second layer of authentication to reduce the risk of unauthorized access.'
        },
        {
            title: 'SSO (Single Sign-On)',
            description: 'Allows centralized user authentication to improve security and user experience.'
        },
        {
            title: 'Conditional Access',
            description: 'Controls access to resources based on contextual factors like device health and location.'
        },
        {
            title: 'Device Certificates',
            description: 'Provides cryptographic proof of device identity for secure access enforcement.'
        },
        {
            title: 'Co-Managed EDR',
            description: 'Ensures endpoint visibility and threat detection through shared management of endpoint protection tools.'
        },
        {
            title: 'USB/Bluetooth Control',
            description: 'Restricts or monitors use of physical interfaces to prevent data leakage and malware introduction.'
        },
        {
            title: 'App Whitelisting',
            description: 'Allows only approved applications to run, reducing the risk of executing unauthorized software.'
        },
        {
            title: 'Encryption in Transit & at Rest',
            description: 'Protects data confidentiality during transmission and while stored.'
        },
        {
            title: 'Azure Key Management',
            description: 'Secures cryptographic keys used for data protection within Azure environments.'
        },
        {
            title: 'Anti-phishing Email Security',
            description: 'Detects and blocks phishing emails to prevent credential theft and social engineering attacks.'
        },
        {
            title: 'SPF, DKIM, DMARC',
            description: 'Authenticates email senders and helps prevent spoofing and phishing.'
        },
        {
            title: 'Off-site Encrypted Backups',
            description: 'Stores critical data in secure, geographically separate locations to ensure availability during disasters.'
        },
        {
            title: 'Backup Testing',
            description: 'Validates backup integrity and recovery processes to ensure data can be restored when needed.'
        },
        {
            title: 'Zero Trust Network Architecture',
            description: 'Implements strict access controls assuming no implicit trust inside or outside the network perimeter.'
        },
        {
            title: 'Azure NSG Hardening',
            description: 'Enforces strict traffic filtering rules at the Azure network level to minimize attack surface.'
        },
        {
            title: 'Code & IaC Scanning',
            description: 'Detects security vulnerabilities in application and infrastructure code before deployment.'
        },
        {
            title: 'Adversarial AI/ML/LLM Mitigations',
            description: 'Protects machine learning models against manipulation and exploitation.'
        },
        {
            title: 'AI Output Filtering',
            description: 'Monitors and restricts LLM-generated output to prevent leakage or harmful responses.'
        },
        {
            title: 'Model Monitoring',
            description: 'Tracks AI model behavior for signs of misuse, drift, or compromise.'
        },
        {
            title: 'Centralized Logging',
            description: 'Aggregates log data from across the environment for threat detection and forensic investigation.'
        }
    ];

    return (
        <div className="controls-page">
            <section className="controls-hero">
                <div className="controls-hero-content">
                    <Link to="/trust-center" className="back-link">
                        <ArrowLeft size={20} /> Back to Trust Center
                    </Link>
                    <h1 className="controls-hero-title">Organizational & Technical Controls</h1>
                </div>
            </section>

            <section className="controls-content">
                <div className="controls-container">
                    <div className="controls-section">
                        <div className="section-header">
                            <Shield size={32} />
                            <h2>Organizational Controls</h2>
                        </div>
                        <div className="controls-grid">
                            {organizationalControls.map((control, index) => (
                                <div key={index} className="control-card">
                                    <h3>{control.title}</h3>
                                    <p>{control.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="controls-section">
                        <div className="section-header">
                            <Lock size={32} />
                            <h2>Technical Controls</h2>
                        </div>
                        <div className="controls-grid">
                            {technicalControls.map((control, index) => (
                                <div key={index} className="control-card">
                                    <h3>{control.title}</h3>
                                    <p>{control.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Controls;
